import { useState, useEffect, useCallback, forwardRef, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';

export type ReadymadeCategory = {
  id: number;
  name: string;
};

type ReadymadeCategoryModalProps = {
  selectedCategory: ReadymadeCategory | null;
  onSelect: (category: ReadymadeCategory | null) => void;
};

const ReadymadeCategoryModal = forwardRef<ModalMethods, ReadymadeCategoryModalProps>(
  ({ selectedCategory, onSelect }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [categories, setCategories] = useState<ReadymadeCategory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [editingCategory, setEditingCategory] = useState<ReadymadeCategory | null>(null);
    const [editingName, setEditingName] = useState('');

    const fetchCategories = useCallback(async (query: string = '') => {
      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-category-boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const encodedQuery = encodeURIComponent(query.trim());
        const response = await api.getRequest(`readymade/categories?query=${encodedQuery || ''}`);

        if (response.status && !_isNil(response.data)) {
          const data = Array.isArray(response.data) ? response.data : [];
          const valid = data.filter(
            (item: ReadymadeCategory) => item.name && !_isEmpty(item.name.trim())
          );

          setCategories(valid);
        } else {
          toasts(
            'error',
            response.message || 'Failed to fetch categories',
            'readymade-categories-load-error'
          );
          setCategories([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-categories-load-error');
        }
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    const debouncedFetchRef = useRef(
      debounce((query: string) => {
        void fetchCategories(query);
      }, 500)
    );

    useEffect(() => {
      const trimmed = searchQuery.trim();

      if (_isEmpty(trimmed)) {
        debouncedFetchRef.current.cancel();
        void fetchCategories('');
      } else {
        debouncedFetchRef.current(trimmed);
      }
    }, [searchQuery, fetchCategories]);

    useEffect(() => {
      return () => {
        debouncedFetchRef.current.cancel();
      };
    }, []);

    const handleSelect = (category: ReadymadeCategory) => {
      onSelect(category);

      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const startEdit = (category: ReadymadeCategory) => {
      setEditingCategory(category);
      setEditingName(category.name);
    };

    const handleEditSave = async () => {
      if (!editingCategory) {
        return;
      }

      const trimmed = editingName.trim();

      if (_isEmpty(trimmed)) {
        toasts('error', 'Please enter a category name', 'readymade-category-edit-name-required');

        return;
      }

      try {
        const encodedName = encodeURIComponent(trimmed);
        const response = await api.putRequest(
          `readymade/categories/${editingCategory.id}?name=${encodedName}`,
          {}
        );

        if (response.status && !_isNil(response.data)) {
          const updatedCategory: ReadymadeCategory = { ...editingCategory, name: trimmed };

          setCategories((prev) =>
            prev.map((c) => (c.id === updatedCategory.id ? updatedCategory : c))
          );

          if (selectedCategory?.id === updatedCategory.id) {
            onSelect(updatedCategory);
          }

          setEditingCategory(null);
          setEditingName('');
          toasts('success', 'Category updated successfully', 'readymade-category-update-success');
        } else {
          toasts(
            'error',
            response.message || 'Failed to update category',
            'readymade-category-update-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-category-update-error');
        }
      }
    };

    const handleDelete = async (category: ReadymadeCategory) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete the category "${category.name}"?`
      );

      if (!confirmed) {
        return;
      }

      try {
        const response = await api.deleteRequest(`readymade/categories/${category.id}`);

        if (response.status) {
          setCategories((prev) => prev.filter((c) => c.id !== category.id));

          if (selectedCategory?.id === category.id) {
            onSelect(null);
          }

          toasts('success', 'Category deleted successfully', 'readymade-category-delete-success');
        } else {
          toasts(
            'error',
            response.message || 'Failed to delete category',
            'readymade-category-delete-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-category-delete-error');
        }
      }
    };

    const handleCreate = async () => {
      const trimmed = searchQuery.trim();

      if (_isEmpty(trimmed)) {
        toasts('error', 'Please enter a category name', 'readymade-category-name-required');

        return;
      }

      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-category-boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        const encodedName = encodeURIComponent(trimmed);
        const response = await api.postRequest(`readymade/categories?name=${encodedName}`, {});

        if (response.status && !_isNil(response.data)) {
          const category = response.data as ReadymadeCategory;
          onSelect(category);

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }

          toasts('success', 'Category created successfully', 'readymade-category-create-success');
        } else {
          toasts(
            'error',
            response.message || 'Failed to create category',
            'readymade-category-create-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-category-create-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    const isQueryExisting = categories.some(
      (item) => item.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );

    const showCreate =
      !_isEmpty(searchQuery.trim()) && !isQueryExisting && !isLoading && !isCreating;

    return (
      <Modal ref={ref} size="large" title="Select or add category" showFooter={false} showCloseIcon>
        <div className="flex flex-col h-full min-h-[400px]">
          <div className="mb-3">
            <Text className="font-inter text-sm text-gray-600 mb-2 mt-1 block" color="black">
              Search existing categories or create a new one.
            </Text>
            <div className="relative flex items-center w-full">
              <input
                type="text"
                placeholder="Search or type a new category"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                autoFocus
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader />
            </div>
          ) : categories.length > 0 ? (
            <div className="flex flex-col gap-2 py-2 max-h-[280px] overflow-y-auto overflow-x-hidden">
              {categories.map((category) => {
                const isSelected = selectedCategory?.id === category.id;

                return (
                  <div
                    key={category.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect(category)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(category);
                      }
                    }}
                    className={`w-full px-4 py-3 rounded-lg text-left flex items-center justify-between gap-3 border transition-all cursor-pointer ${
                      isSelected ? 'bg-blue-50 border-blue-500' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Text
                      size="medium"
                      color="black"
                      className="font-inter truncate flex-1 min-w-0"
                    >
                      {category.name}
                    </Text>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(category);
                        }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        aria-label={`Edit ${category.name}`}
                      >
                        <FaPencilAlt size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(category);
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label={`Delete ${category.name}`}
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] p-5">
              <Text color="gray" size="small">
                {searchQuery ? 'No categories found' : 'No categories available'}
              </Text>
            </div>
          )}

          {editingCategory && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <Text className="font-inter text-xs text-gray-500 mb-1" color="black">
                Editing category: {editingCategory.name}
              </Text>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="flex-1 h-9 px-3 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleEditSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingCategory(null);
                    setEditingName('');
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {showCreate && (
            <div className="mt-auto pt-4 pb-5 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : `+ Create "${searchQuery.trim()}" as new Category`}
              </button>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

ReadymadeCategoryModal.displayName = 'ReadymadeCategoryModal';

export default ReadymadeCategoryModal;
